import PlanningToolsLinks from "@/components/PlanningToolsLinks";
import { RelatedHubGrid } from "@/components/RelatedHubGrid";
import AnswerBoxTop from "@/components/AnswerBoxTop";
// /en/dtv — EN hub for Destination Thailand Visa
import { createFileRoute, Link } from "@tanstack/react-router";
import { DTV_CATEGORIES } from "@/data/dtv-categories";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Phone, MessageCircle, ArrowRight, Sparkles, Globe2, Plane, Wallet, CalendarClock, FileText } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/en/dtv/")({
  loader: async () => {
    const { DTV_CATEGORIES: list } = await import("@/data/dtv-categories");
    return { index: list.map((c) => ({ slug: c.slug, name: c.titleEn })) };
  },
  head: ({ loaderData }) => {
    const index = loaderData?.index ?? [];

    const url = `${SITE}/en/dtv`;
    const title = `DTV — Destination Thailand Visa 5 Years | Digital Nomad & Long Stay 2026 | NYC`;
    const desc = `Destination Thailand Visa: 5-year multiple-entry — Workcation, Freelancer, Muay Thai, Thai Cooking, Medical, Sports, Soft Power, Dependent. Full steps, documents, 10,000 THB government fee. Ask us before you file — it's free.`;
    return {
      meta: [
        { title: ctaTitle(title, "/en/dtv", "en") }, { name: "description", content: ctaDesc(desc, "/en/dtv", "en")},
        { property: "og:title", content: ctaTitle(title, "/en/dtv", "en")}, { property: "og:description", content: ctaDesc(desc, "/en/dtv", "en")},
        { property: "og:url", content: url }, { property: "og:locale", content: "en_US" },
        { name: "keywords", content: "DTV visa Thailand, digital nomad visa Thailand, 5-year Thai visa, workcation visa, Muay Thai visa" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks("/dtv", "/en/dtv")],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "CollectionPage", "@id": `${url}#page`,
              name: title, description: desc, url, inLanguage: "en",
              mainEntity: {
                "@type": "ItemList", numberOfItems: index.length,
                itemListElement: index.map((c, i) => ({
                  "@type": "ListItem", position: i + 1, name: c.name,
                  url: `${SITE}/en/dtv/${c.slug}`,
                })),
              },
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: "Destination Thailand Visa (DTV) Application Service",
              serviceType: "Destination Thailand Visa (DTV) Application Service",
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
              areaServed: "TH",
              offers: { "@type": "Offer", price: "9500", priceCurrency: "THB", description: "DTV application + document prep + financial structure" },
            }),
          },
          faqPageSchema([
            { q: "What is DTV?", a: "Destination Thailand Visa (DTV) — a 5-year multiple-entry visa launched by the Royal Thai Government on 15 July 2024 under Ministerial Regulation No.31 (B.E.2567). Each entry permits a 180-day stay, extendable once on-shore for a further 180 days (max 360/year). Government fee THB 10,000, processed via e-Visa or any Royal Thai Embassy worldwide." },
            { q: "What is the financial requirement?", a: "Applicants must show liquid funds of at least THB 500,000 (about USD 14,000) held for the previous 6 months — accepted as a bank statement, fixed deposit, brokerage account, or sponsor letter with supporting documents. NYC pre-checks every statement against the Embassy checklist before submission to avoid rejection." },
            { q: "Can I work in Thailand on DTV?", a: "Remote work for overseas employers or clients only — DTV explicitly excludes employment by a Thai-registered company under Immigration Order 327/2557. Working for a Thai entity (including freelance for a Thai client) requires a Non-B visa plus a Work Permit issued by the Ministry of Labour; NYC handles that conversion in 21 business days." },
            { q: "What if I stay longer than 180 days?", a: "File the standard 90-day TM47 address report online via the Immigration Bureau portal (free), then a one-time 180-day extension at any Immigration office for THB 1,900 — total 360 days per entry. Leaving and re-entering on the same DTV resets the clock. NYC files TM47 on your behalf for THB 1,500." },
            { q: "Can I bring family?", a: "Yes — DTV Dependent visas are available for a legal spouse and unmarried children under 20, at THB 10,000 per dependent. Required documents: marriage/birth certificates with Apostille (Thailand joined Hague 14 Dec 2024) plus certified Thai or English translation by an MoJ-registered translator." },
          ]),
        ]),
      ],
    };
  },
  component: DtvHubEn,
});

function DtvHubEn() {
  return (
    <main className="min-h-screen bg-background">
      <AnswerBoxTop cluster="dtv" locale="en" />
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-16 pb-12">
        <div className="mx-auto max-w-6xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/en" className="hover:text-primary">Home</Link><span className="mx-2">/</span><span>DTV</span>
          </nav>
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold text-primary">
            <Globe2 className="mr-1 inline h-3 w-3" /> Destination Thailand Visa · 9 categories
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">DTV — Destination Thailand Visa</h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            5-year multiple-entry visa for digital nomads, freelancers, Muay Thai and Thai cooking students, medical patients, athletes, soft-power learners, and their families — 180 days per stay, extendable to 360 days/year.
          </p>
          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">What is DTV and who can apply?</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Launched 15 Jul 2024 — 5-year multiple-entry visa with 10,000 THB government fee. Requires 500,000 THB in funds plus a sponsor from one of nine categories (Workcation, Freelancer, Muay Thai, Thai Cooking, Medical, Sports, Seminar, Soft Power, Dependent). Remote work permitted; salary from a Thai employer is not.
            </p>
          </div>
          <div className="mt-5 grid gap-3 sm:grid-cols-4 max-w-3xl">
            <div className="rounded-xl border border-border bg-card p-3"><CalendarClock className="h-4 w-4 text-primary" /><div className="mt-1 text-xs text-muted-foreground">Validity</div><div className="text-sm font-bold">5 years</div></div>
            <div className="rounded-xl border border-border bg-card p-3"><Plane className="h-4 w-4 text-primary" /><div className="mt-1 text-xs text-muted-foreground">Entries</div><div className="text-sm font-bold">Unlimited</div></div>
            <div className="rounded-xl border border-border bg-card p-3"><Wallet className="h-4 w-4 text-primary" /><div className="mt-1 text-xs text-muted-foreground">Gov fee</div><div className="text-sm font-bold">10,000 THB</div></div>
            <div className="rounded-xl border border-border bg-card p-3"><FileText className="h-4 w-4 text-primary" /><div className="mt-1 text-xs text-muted-foreground">Stay/entry</div><div className="text-sm font-bold">180+180 d</div></div>
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> Call +66 92 242 9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
            <Link to="/en/visa" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <FileText className="h-4 w-4" /> All visa types
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">9 DTV categories</h2>
          <p className="mt-2 text-sm text-muted-foreground">Pick the category that matches your sponsor · Click for docs, steps, and pricing</p>
          <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {DTV_CATEGORIES.map((c) => (
              <Link
                key={c.slug}
                to="/en/dtv/$category"
                params={{ category: c.slug }}
                className="group rounded-2xl border border-border bg-card p-4 transition hover:-translate-y-0.5 hover:border-primary hover:shadow-md"
                data-track-zone="dtv-hub-en"
              >
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{c.icon}</span>
                  <div className="font-bold text-sm">{c.titleEn}</div>
                </div>
                <div className="mt-2 line-clamp-2 text-xs text-muted-foreground">{c.taglineEn}</div>
                <div className="mt-3 flex items-center justify-between text-xs">
                  <span className="rounded-full bg-muted px-2 py-0.5 font-semibold">THB {c.servicePriceTHB.toLocaleString()}</span>
                  <span className="inline-flex items-center gap-1 font-semibold text-primary">
                    View <ArrowRight className="h-3 w-3" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-muted/30 px-4 py-10">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-xl font-extrabold">Related services</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { t: "Visa Matrix — compare 7 visas", d: "DTV vs LTR vs Non-O in 60 seconds", to: "/en/visa-matrix" as const },
              { t: "Visa Compliance", d: "TM30 / 90-day / Re-entry", to: "/en/visa-compliance" as const },
              { t: "All Visa Types", d: "Non-B / LTR / Retirement / Marriage", to: "/en/visa" as const },
              { t: "Driving License", d: "Convert foreign license", to: "/en/driving-license" as const },
              { t: "Certified Translation", d: "Marriage cert, employer letter", to: "/en/translate" as const },
              { t: "Apostille", d: "For foreign documents", to: "/en/apostille" as const },
              { t: "Work Permit", d: "If you'll work for a Thai company", to: "/en/work-permit" as const },
            ].map((s) => (
              <Link key={s.t} to={s.to} className="rounded-xl border border-border bg-card p-4 hover:border-primary">
                <div className="font-bold">{s.t}</div>
                <div className="mt-1 text-xs text-muted-foreground">{s.d}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <TrustLayer variant="compact" />
    <RelatedHubGrid locale="en" />
      <PlanningToolsLinks locale="en" />
      </main>
  );
}
